import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ContractStep, emptyContract } from "../components/samourais/ContractStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InscriptionSamouraisPublic() {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);
  const [contract, setContract] = useState(emptyContract());
  const [wl, setWl] = useState({ child_name: "", child_age: "", parent_name: "", parent_email: "", parent_phone: "", discipline_interest: "" });
  const [form, setForm] = useState({
    child_name: "", child_age: "", parent_name: "", parent_email: "", parent_phone: "",
    formule: "essai", uniforme_enfant: false, uniforme_parent: false,
  });

  useEffect(() => {
    axios.get(`${API}/samourais/public/info`)
      .then((r) => {
        setInfo(r.data);
        const open = (r.data.sessions || []).filter((s) => !s.full);
        if (open.length) setSessionId(open[0].id);
        else if (r.data.sessions?.length) setSessionId(r.data.sessions[0].id);
      })
      .catch(() => setErr("Impossible de charger les informations du programme."));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const total = useMemo(() => {
    if (!info) return 0;
    let t = info.prices?.[form.formule] || 0;
    if (form.formule !== "essai") t += info.registration_fee || 0;
    if (info.extras_enabled && form.uniforme_enfant) t += info.uniform_child || 0;
    if (info.extras_enabled && form.uniforme_parent) t += info.uniform_parent || 0;
    return t;
  }, [info, form]);

  const goToContract = (e) => {
    e.preventDefault();
    if (!sessionId) { toast.error("Choisis une session"); return; }
    if (!form.child_name.trim()) { toast.error("Le nom de l'enfant est requis"); return; }
    if (!form.parent_name.trim() || !form.parent_email.trim()) {
      toast.error("Nom et courriel du parent requis"); return;
    }
    setContract((p) => ({ ...p, signature_name: p.signature_name || "" }));
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/samourais/public/register`, {
        session_id: sessionId, ...form,
        child_age: form.child_age ? parseInt(form.child_age) : null,
        contacts_urgence: contract.contacts_urgence.filter((c) => c.nom || c.telephone),
        terms_accepted: contract.terms_accepted, waiver_accepted: contract.waiver_accepted, photo_consent: !!contract.photo_consent,
        signature_name: contract.signature_name, consent_version: "ps-2026-09",
      });
      if (r.data.waitlisted) { toast.success(r.data.message); setBusy(false); return; }
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      toast.success(r.data.message || "Préinscription enregistrée !");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Une erreur est survenue.");
    }
    setBusy(false);
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!wl.child_name.trim()) { toast.error("Le nom de l'enfant est requis"); return; }
    if (!wl.parent_name.trim() || !wl.parent_email.trim()) { toast.error("Nom et courriel du parent requis"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/samourais/public/waitlist`, {
        ...wl, child_age: wl.child_age ? parseInt(wl.child_age) : null,
      });
      toast.success(r.data.message || "Tu es sur la liste d'attente !");
      setWl({ child_name: "", child_age: "", parent_name: "", parent_email: "", parent_phone: "", discipline_interest: "" });
    } catch (e2) { toast.error(e2.response?.data?.detail || "Une erreur est survenue."); }
    setBusy(false);
  };

  if (err) return <div className="min-h-screen bg-black text-white flex items-center justify-center p-6 text-center">{err}</div>;
  if (!info) return <div className="min-h-screen bg-black text-white flex items-center justify-center">Chargement…</div>;

  const sessions = info.sessions || [];
  const noSession = sessions.length === 0;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="samourais-public">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="text-center mb-8">
          <div className="text-5xl mb-2">🥋</div>
          <h1 className="text-3xl sm:text-4xl font-bold">{info.program}</h1>
          <p className="text-zinc-400 mt-2">{info.subtitle}</p>
          <p className="text-zinc-500 text-sm mt-1">Institut Impact Élite Martial · {info.city}</p>
        </div>

        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 mb-4 text-center" data-testid="samourais-rentree">
          <div className="text-2xl mb-1">🗓️</div>
          <div className="text-red-200 font-bold">Rentrée : {info.rentree || "Mercredi 9 septembre 2026"}</div>
          <div className="text-xs text-zinc-400 mt-1">Places limitées — réserve tôt pour garantir la place de ton enfant.</div>
        </div>

        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6 text-sm text-amber-100/90">
          Un premier pas en arts martiaux à vivre <strong>ensemble</strong> : confiance, écoute et plaisir,
          dans un cadre rassurant. Places limitées pour garantir la qualité du groupe.
        </div>

        {(() => {
          const count = noSession ? (info.waitlist_count || 0) : (info.enrolled_count || 0);
          if (count < 1) return null;
          const label = noSession
            ? `${count} famille${count > 1 ? "s" : ""} déjà sur la liste d'attente`
            : `${count} famille${count > 1 ? "s" : ""} déjà préinscrite${count > 1 ? "s" : ""}`;
          return (
            <div data-testid="samourais-social-proof"
              className="flex items-center justify-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 mb-6 text-sm text-emerald-200 font-medium">
              <span className="text-lg">🥋</span> {label}
            </div>
          );
        })()}

        {!noSession && (
          <div className="flex items-center gap-2 mb-5 text-xs" data-testid="samourais-steps">
            {[["1", "Enfant & formule"], ["2", "Contrat & signature"]].map(([n, label]) => (
              <div key={n} className={`flex-1 flex items-center gap-2 rounded-full border px-3 py-1.5 ${step === Number(n) ? "border-red-500 bg-red-500/10 text-white" : "border-white/10 text-zinc-500"}`} data-testid={`samourais-step-${n}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold ${step === Number(n) ? "bg-red-600 text-white" : "bg-white/10"}`}>{n}</span>{label}
              </div>
            ))}
          </div>
        )}

        {step === 2 && !noSession && (
          <ContractStep form={form} total={total} contract={contract} setContract={setContract}
            onBack={() => setStep(1)} onSubmit={submit} busy={busy} />
        )}

        <form onSubmit={goToContract} className={`space-y-5 ${noSession || step !== 1 ? "hidden" : ""}`}>
          {/* Session */}
          <div>
            <label className="block text-sm font-medium mb-2">Bloc / session</label>
            <div className="space-y-2">
              {sessions.map((s) => (
                <button type="button" key={s.id} disabled={s.full}
                  data-testid={`samourais-session-${s.id}`}
                  onClick={() => setSessionId(s.id)}
                  className={`w-full text-left rounded-xl border p-3 transition-colors ${sessionId === s.id ? "border-red-500 bg-red-500/10" : "border-white/10 bg-white/5 hover:bg-white/10"} ${s.full ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <div className="font-medium">{s.title}</div>
                  <div className="text-xs text-zinc-400">{s.days} · {s.time} · {s.weeks} sem. · début {s.start_date}</div>
                  <div className="text-xs mt-1">{s.full ? <span className="text-red-300">Complet — liste d'attente</span> : <span className="text-emerald-300">{s.spots_left} place(s) restante(s)</span>}</div>
                </button>
              ))}
              {sessions.length === 0 && <p className="text-zinc-500 text-sm">Aucune session ouverte pour le moment.</p>}
            </div>
          </div>

          {/* Formule */}
          <div>
            <label className="block text-sm font-medium mb-2">Formule <span className="text-xs text-zinc-400">({info.price_period || "au 4 semaines · taxes incluses"})</span></label>
            <select data-testid="samourais-formule" value={form.formule} onChange={(e) => set("formule", e.target.value)}
              className="w-full rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm">
              {Object.entries(info.formule_labels).map(([k, label]) => (
                <option key={k} value={k}>{label} — {Number(info.prices[k]).toFixed(2)} $</option>
              ))}
            </select>
          </div>

          {/* Enfant + parent */}
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="samourais-child-name" placeholder="Nom de l'enfant *" value={form.child_name}
              onChange={(e) => set("child_name", e.target.value)}
              className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
            <input data-testid="samourais-child-age" type="number" min="3" max="6" placeholder="Âge (3-6)" value={form.child_age}
              onChange={(e) => set("child_age", e.target.value)}
              className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
            <input data-testid="samourais-parent-name" placeholder="Nom du parent *" value={form.parent_name}
              onChange={(e) => set("parent_name", e.target.value)}
              className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2" />
            <input data-testid="samourais-parent-email" type="email" placeholder="Courriel *" value={form.parent_email}
              onChange={(e) => set("parent_email", e.target.value)}
              className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
            <input data-testid="samourais-parent-phone" type="tel" placeholder="Téléphone" value={form.parent_phone}
              onChange={(e) => set("parent_phone", e.target.value)}
              className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
          </div>

          {/* Uniformes — extras désactivés tant que les prix ne sont pas validés */}
          {info.extras_enabled ? (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="samourais-uni-child" checked={form.uniforme_enfant} onChange={(e) => set("uniforme_enfant", e.target.checked)} />
                Uniforme enfant (+{info.uniform_child} $)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="samourais-uni-parent" checked={form.uniforme_parent} onChange={(e) => set("uniforme_parent", e.target.checked)} />
                Uniforme parent (+{info.uniform_parent} $)
              </label>
            </div>
          ) : (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm space-y-2" data-testid="samourais-equipment-block">
              <div className="font-semibold text-amber-300">🥋 Équipement Petits Samouraïs — prix confirmés au dojo avant toute commande</div>
              <div className="grid sm:grid-cols-2 gap-3 text-xs text-white/80">
                <div><div className="font-semibold text-white mb-1">Pour l'enfant</div>• Petit pantalon de karaté simple ou pantalon d'entraînement confortable<br/>• T-shirt à manches courtes avec le logo de l'Institut Impact<br/>• Petite paire de gants de boxe adaptée à l'enfant<br/>• Bouteille d'eau identifiée</div>
                <div><div className="font-semibold text-white mb-1">Pour le parent accompagnateur</div>• Vêtements confortables permettant de bouger<br/>• T-shirt à manches courtes avec logo du dojo, lorsque requis ou disponible<br/>• Petite paire de gants de boxe, selon les activités prévues<br/>• Bouteille d'eau identifiée</div>
              </div>
              <p className="text-[11px] text-white/60">Les uniformes et les gants peuvent être commandés au dojo, ou achetés ailleurs s'ils respectent les consignes de sécurité et le format demandé. Aucun équipement n'est facturé en ligne : prix, tailles et disponibilités sont confirmés avec l'instructeur avant toute commande.</p>
              <a href="/petits-samourais/equipement" target="_blank" rel="noreferrer" className="inline-block text-xs font-semibold text-amber-300 underline" data-testid="samourais-equipment-link">🥋 Voir les tailles, photos et réserver pour la commande groupée →</a>
            </div>
          )}

          <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-4">
            <span className="text-zinc-400">Total<span className="block text-[11px] text-zinc-500">{form.formule === "essai" ? "1 cours d'essai · taxes incluses" : `premier bloc de 4 semaines + frais d'inscription ${Number(info.registration_fee || 0).toFixed(0)} $ · taxes incluses`}</span></span>
            <span className="text-2xl font-bold" data-testid="samourais-total-step1">{total.toFixed(2)} $</span>
          </div>

          <button type="submit" disabled={busy} data-testid="samourais-continue"
            className="w-full rounded-xl bg-red-600 hover:bg-red-500 py-3.5 font-semibold transition-colors disabled:opacity-50">
            Continuer → Contrat & signature
          </button>
          <p className="text-center text-xs text-zinc-500">Étape suivante : contacts d'urgence, conditions, décharge et signature, puis paiement sécurisé Stripe.</p>
        </form>

        {noSession && (
          <form onSubmit={submitWaitlist} className="space-y-4" data-testid="samourais-waitlist-form">
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-100/90">
              Aucun groupe n'est ouvert pour l'instant. Laisse-nous tes coordonnées : tu seras <strong>le premier averti</strong> dès l'ouverture du prochain groupe Petits Samouraïs !
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="wl-child-name" placeholder="Nom de l'enfant *" value={wl.child_name}
                onChange={(e) => setWl({ ...wl, child_name: e.target.value })}
                className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
              <input data-testid="wl-child-age" type="number" min="3" max="6" placeholder="Âge (3-6)" value={wl.child_age}
                onChange={(e) => setWl({ ...wl, child_age: e.target.value })}
                className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
              <input data-testid="wl-parent-name" placeholder="Nom du parent *" value={wl.parent_name}
                onChange={(e) => setWl({ ...wl, parent_name: e.target.value })}
                className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2" />
              <input data-testid="wl-parent-email" type="email" placeholder="Courriel *" value={wl.parent_email}
                onChange={(e) => setWl({ ...wl, parent_email: e.target.value })}
                className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
              <input data-testid="wl-parent-phone" type="tel" placeholder="Téléphone" value={wl.parent_phone}
                onChange={(e) => setWl({ ...wl, parent_phone: e.target.value })}
                className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 sm:col-span-1" />
              <select data-testid="wl-discipline" value={wl.discipline_interest}
                onChange={(e) => setWl({ ...wl, discipline_interest: e.target.value })}
                className="rounded-xl bg-zinc-900 border border-white/10 p-3 text-sm col-span-2 text-zinc-100 [&>option]:bg-zinc-900">
                <option value="">Discipline d'intérêt (optionnel)</option>
                <option value="petits_samourais">Petits Samouraïs (3-6 ans)</option>
                <option value="enfants">Enfants (7-12 ans)</option>
                <option value="ados">Ados (13-17 ans)</option>
                <option value="autre">Autre / pas sûr</option>
              </select>
            </div>
            <button type="submit" disabled={busy} data-testid="wl-submit"
              className="w-full rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 py-3.5 font-semibold transition-colors disabled:opacity-50">
              {busy ? "Traitement…" : "Rejoindre la liste d'attente"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
